import { Component, ViewEncapsulation, HostBinding, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { Observable, Subscription, map, catchError } from 'rxjs';
import { MatRippleModule } from '@angular/material/core';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { WEIGHT_OPTIONS, ORDER_NUMBER_OPTIONS, PRODUCT_OPTIONS } from '../../../shared/models/list';
import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { UNDERSCORE, OrderByDirection, OrderBy } from '../../../shared/utils/constant';
import { ARTICLE, CUSTOMER, GROSS } from '../../../shared/models/settings';
import { Shipment, FieldOfShipment } from '../../models/shipment';
import { ShipmentsService } from '../../services/shipments.service';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';

const sortWay = OrderByDirection.DESC;
const sortField = 'dispatchAdviceNumber';

@Component({
	selector: 'copo-search-shipment-list',
	templateUrl: './search-shipment-list.component.html',
	animations: pageTransitions,
	styleUrl: './search-shipment-list.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		VirtualScrollerDirective,
		CdkScrollable,
		TranslateModule,
		NgFor,
		MatRippleModule,
		RouterLink,
		NgIf,
		LoadingSpinnerComponent,
		NumberPipe
	]
})
export class SearchShipmentListComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	user: User;
	productDisplay: keyof typeof PRODUCT_OPTIONS = ARTICLE;
	orderDisplay: keyof typeof ORDER_NUMBER_OPTIONS = CUSTOMER;
	weightDisplay: keyof typeof WEIGHT_OPTIONS = GROSS;
	filter?: string | undefined;

	readonly UNDERSCORE = UNDERSCORE;

	shipmentRequiredFields: FieldOfShipment[] = [
		'dispatchAdviceNumber',
		'soldToDescription',
		'qtyBatchBrutConcat',
		'transportReferenceConcat',
		'transportModeDescription',
		'transportMode',
		'qtyBatchNetConcat',
		'qtyShipmentBrut',
		'qtyShipmentNet',
		'transportMode',
		'shipTo',
		'shipToDescription',
		'supplierShipmentNumber',
		'shipmentReference',
		'shipmentReferenceDate',
		'currentLocationConcat',
		'currentLocationDescriptionConcat',
		'shipToAddressConcat',
		'deliveryModeConcat',
		'deliveryModeDescriptionConcat',
		'batchStatusConcat',
		'batchStatusDescriptionConcat',
		'endUserConcat',
		'endUserDescriptionConcat',
		'heatNumberConcat',
		'millOrderNumberConcat',
		'packageSerialNumberConcat',
		'destinationByBoatConcat',
		'productCategoryStuConcat',
		'productCategoryStuDescriptionConcat',
		'serviceLevelConcat',
		'serviceLevelDescriptionConcat',
		'supplierInvoiceNumberConcat',
		'htsCodeConcat',
		'producedLengthDimensionConcat',
		'salesQuarterConcat',
		'sfccConcat',
		'sfccDescriptionConcat',
		'choice',
		'choiceDescription',
		'choiceDescriptionConcat',
		'productDescriptionStu',
		'readyToShipDateConcat',
		'specialStockPartnerConcat',
		'specialStockPartnerDescriptionConcat',
		'formConcat',
		'formDescriptionConcat',
		'endUser',
		'endUserDescription',
		'choiceConcat',
		'productCategory',
		'productCategoryDescription',
		'sfcc',
		'sfccDescription',
		'stockPartner',
		'stockPartnerDescription',
		'batchStatusConcat',
		'batchStatus',
		'status',
		'statusDescription'
	];

	private readonly searchTextSubscription?: Subscription;

	constructor(
		private readonly shipmentService: ShipmentsService,
		private readonly userService: UserService,
		private readonly router: Router,
		private readonly activedRoute: ActivatedRoute
	) {
		this.user = this.userService.user;
		this.shipmentRequiredFields.push('qtyBatchBrutConcat');
		if (!this.router.isActive(
			'search',
			{ paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' }
		)) {
			return;
		}
		this.searchTextSubscription = this.activedRoute.params.subscribe(p => {
			const searchText = <string | undefined> p['query'];
			this.filter = searchText ? this.shipmentService.createLuceneFilter(searchText) : undefined;
		});
	}

	get dataId() {
		const dataId = 'search-shipment-list';
		return this.filter === undefined ? dataId : `${ dataId }-${ this.filter }`;
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<Shipment[]>) => void) {
		provideData(
			this.shipmentService.getDatacenterShipments(
				this.shipmentRequiredFields,
				this.filter,
				index,
				limit,
				new OrderBy(sortField, sortWay)
			).pipe(
				map(res => res.results),
				catchError((err: unknown) => {
					console.error('error retrieving batches', err);
					return [];
				})
			)
		);
	}
}
